import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { PublicationEditionModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realhub/publicationEdition/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/publicationEdition/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/publicationEdition/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/publicationEdition/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/publicationEdition/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/publicationEdition/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/publicationEdition/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/publicationEdition/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/publicationEdition/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/publicationEdition/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/publicationEdition/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/publicationEdition/DELETE_FAILURE'

// Initial State
export type PublicationEditionModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: PublicationEditionModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPublicationEditions(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  // @ts-expect-error TS7006
  return (dispatch, getState) => {
    const state = getState()
    const loadedForKeys = [...state.publicationEditions.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_editions.json', options)
      .then(({ data }: { data: Partial<PublicationEditionModel>[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPublicationEdition(publicationEditionId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.publicationEditions.loadedForKeys]
    const loadedIds = [...state.publicationEditions.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (publicationEditionId && !loadedIds.includes(publicationEditionId)){
      loadedIds.push(publicationEditionId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/publication_editions/${publicationEditionId}.json`, options)
      .then(({ data }: { data: PublicationEditionModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPublicationEditionDeadlines(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  // @ts-expect-error TS7006
  return (dispatch, getState) => {
    const state = getState()
    const loadedForKeys = [...state.publicationEditions.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_editions/upcoming_deadlines.json', options)
      .then(({ data }: { data: Partial<PublicationEditionModel>[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAvailableLiftovers(
  orderItemId: number,
  options: EntityOptions = {},
) {
  const mergedOptions = {
    order_item_id: orderItemId,
    ...options,
  }

  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/publication_editions/available_liftovers.json', mergedOptions)
      .then(({ data }: { data: PublicationEditionModel[] }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicationEdition(
  publicationEdition: PublicationEditionModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_edition: publicationEdition,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_editions.json', options, config)
      .then(({ data }: { data: PublicationEditionModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePublicationEdition(
  publicationEdition: PublicationEditionModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_edition: publicationEdition,
    }),
  }

  // @ts-expect-error TS7006
  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/publication_editions/${publicationEdition.id}.json`, options, config)
      .then(({ data }: { data: PublicationEditionModel }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePublicationEdition(
  publicationEdition: PublicationEditionModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'DELETE',
  }

  // @ts-expect-error TS7006
  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/publication_editions/${publicationEdition.id}.json`, options, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function autoGenerate(publicationId: number, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      publication_id: publicationId,
    }),
  }

  // @ts-expect-error TS7006
  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/publication_editions/auto_generate.json', options, config)
      .then(({ data }: { data: Partial<PublicationEditionModel>[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function rebuildDeadlines(publicationId: number, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_id: publicationId,
    }),
  }

  // @ts-expect-error TS7006
  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api('/publication_editions/rebuild_deadlines.json', options, config)
      .then(({ data }: { data: Partial<PublicationEditionModel>[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_EDITION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PublicationEditionModuleState = initialState,
  action: Action = { type: '' },
) {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
